import { create } from 'zustand';
import { authService } from '@/services/auth.service';
import { ApiError } from '@/lib/api-client';
import type { AuthUser, UserProfile } from '@/types/auth';
import { bindSidebarPrefsToUser } from '@/stores/use-sidebar-prefs-store';

const USER_KEY = 'bbpos_user';

interface AuthState {
  user: AuthUser | null;
  profile: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrated: boolean;

  login: (email: string, password: string) => Promise<void>;
  verifyOtp: (email: string, otp: string) => Promise<void>;
  verifyTotp: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<number>;
  refreshSession: () => Promise<void>;
  fetchProfile: () => Promise<void>;
  hydrate: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  isAuthenticated: false,
  isLoading: false,
  isHydrated: false,

  login: async (email, password) => {
    const data = await authService.login(email, password);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    set({
      user: data.user,
      isAuthenticated: true,
    });
    await get().fetchProfile();
  },

  verifyOtp: async (email, otp) => {
    const data = await authService.verifyOtp(email, otp);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    set({
      user: data.user,
      isAuthenticated: true,
    });
    await get().fetchProfile();
  },

  verifyTotp: async (email, code) => {
    const data = await authService.verifyTotp(email, code);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    set({
      user: data.user,
      isAuthenticated: true,
    });
    await get().fetchProfile();
  },

  logout: async () => {
    try {
      await authService.logout();
    } catch {
      // Ignore errors — clear local state regardless
    }
    localStorage.removeItem(USER_KEY);
    set({
      user: null,
      profile: null,
      isAuthenticated: false,
    });
  },

  // Unlike logout, a failure here is not swallowed: if the call did not reach
  // the server, no session was ended anywhere, and clearing local state would
  // tell the user their account is secure when it is not.
  logoutAll: async () => {
    const data = await authService.logoutAll();
    localStorage.removeItem(USER_KEY);
    set({
      user: null,
      profile: null,
      isAuthenticated: false,
    });
    return data.sessions_ended;
  },

  refreshSession: async () => {
    try {
      const data = await authService.refreshSession();
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      set({
        user: data.user,
        isAuthenticated: true,
      });
    } catch {
      localStorage.removeItem(USER_KEY);
      set({
        user: null,
        profile: null,
        isAuthenticated: false,
      });
    }
  },

  fetchProfile: async () => {
    try {
      const profile = await authService.getMe();
      set({ profile });
    } catch {
      localStorage.removeItem(USER_KEY);
      set({
        user: null,
        profile: null,
        isAuthenticated: false,
      });
    }
  },

  hydrate: () => {
    const userJson = localStorage.getItem(USER_KEY);

    if (userJson) {
      try {
        const user = JSON.parse(userJson) as AuthUser;
        set({
          user,
          isAuthenticated: true,
          isLoading: true,
        });
        // Validate session by fetching profile (cookie sent automatically)
        authService
          .getMe()
          .then((profile) => {
            set({ profile, isLoading: false, isHydrated: true });
          })
          .catch((err: unknown) => {
            // Only the server rejecting the session ends it. A fetch that never got
            // an answer — typically aborted because the user navigated or reloaded
            // while this check was in flight — must not wipe the stored user, or
            // the next page load starts signed out.
            if (err instanceof ApiError) localStorage.removeItem(USER_KEY);
            set({
              user: null,
              profile: null,
              isAuthenticated: false,
              isLoading: false,
              isHydrated: true,
            });
          });
      } catch {
        localStorage.removeItem(USER_KEY);
        set({ isLoading: false, isHydrated: true });
      }
    } else {
      set({ isLoading: false, isHydrated: true });
    }
  },
}));

// Sidebar preferences follow whoever is signed in (see use-sidebar-prefs-store).
useAuthStore.subscribe((s) => bindSidebarPrefsToUser(s.user?.sub ?? null));
